import React, { useMemo } from 'react';

import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { PurchaseOrderSectionId } from '../../purchase-order-expandable-section-provider';
import { ExpandableSection } from '../expandable-section';
import { messages } from './shipping-details.messages';
import styles from './shipping-details.styles.module.less';
import UserDateTimezoneFormat from '../../../../../components/user-date-timezone-format';

/**
 * Shipping Details component
 * Displays the shipping details for a purchase order
 */
export const ShippingDetails = ({ purchaseOrder, carriersList }) => {
  const { carrierId, trackingNumber, orderNumber, referenceNumber, orderDate, expectedDeliveryDate } = purchaseOrder;

  const carrierName = useMemo(() => {
    if (!carrierId || !carriersList?.length) return '';
    const carrier = carriersList.find((c) => c.value === carrierId);
    return carrier?.label || '';
  }, [carrierId, carriersList]);

  const displayOrderDate = orderDate ? <UserDateTimezoneFormat dateTime={orderDate} /> : '-';
  const displayExpectedDeliveryDate = expectedDeliveryDate ? <UserDateTimezoneFormat dateTime={expectedDeliveryDate} /> : '-';

  // Header content for the expandable section
  const headerContent = <Text textType={TextType.H4} value={messages.shippingDetails} />;

  // Content to display when the section is expanded
  const shippingDetailsContent = (
    <div>
      <FlexRow className={styles['details-grid']}>
        <FlexColumn className={styles['details-column']}>
          <Text className={styles.label} textType={TextType.Label} value={messages.shippingCarrier} />
          <Text className={styles.value} textType={TextType.Paragraph} value={carrierName || '-'} />
        </FlexColumn>
        <FlexColumn className={styles['details-column']}>
          <Text className={styles.label} textType={TextType.Label} value={messages.trackingNumber} />
          <Text className={styles.value} textType={TextType.Paragraph} value={trackingNumber || '-'} />
        </FlexColumn>
      </FlexRow>

      <FlexRow className={styles['details-grid']}>
        <FlexColumn className={styles['details-column']}>
          <Text className={styles.label} textType={TextType.Label} value={messages.orderId} />
          <Text className={styles.value} textType={TextType.Paragraph} value={orderNumber || '-'} />
        </FlexColumn>
        <FlexColumn className={styles['details-column']}>
          <Text className={styles.label} textType={TextType.Label} value={messages.referenceNumber} />
          <Text className={styles.value} textType={TextType.Paragraph} value={referenceNumber || '-'} />
        </FlexColumn>
      </FlexRow>

      <FlexRow className={styles['details-grid']}>
        <FlexColumn className={styles['details-column']}>
          <Text className={styles.label} textType={TextType.Label} value={messages.orderDate} />
          <Text className={styles.value} textType={TextType.Paragraph} value={displayOrderDate} />
        </FlexColumn>
        <FlexColumn className={styles['details-column']}>
          <Text className={styles.label} textType={TextType.Label} value={messages.expectedDeliveryDate} />
          <Text className={styles.value} textType={TextType.Paragraph} value={displayExpectedDeliveryDate} />
        </FlexColumn>
      </FlexRow>
    </div>
  );

  return (
    <ExpandableSection
      ariaLabelMessageDescriptor={messages.shippingDetails}
      className={styles['shipping-details-wrapper']}
      contentClassName={styles['expandable-content-container']}
      headerClassName={styles['expandable-header-container']}
      headerContent={headerContent}
      sectionContent={shippingDetailsContent}
      sectionId={PurchaseOrderSectionId.ShippingDetails}
    />
  );
};
